import React, { useRef, useCallback } from 'react';
import { ScrollView, RefreshControl, View, ActivityIndicator, NativeSyntheticEvent, NativeScrollEvent } from 'react-native';

export interface DbScrollListProps<T> {
    data: T[];
    renderItem: (item: T, index: number) => React.ReactNode;
    keyExtractor?: (item: T, index: number) => string;
    onEndReached?: () => void;         // 触底加载
    onRefresh?: () => void;            // 下拉刷新
    refreshing?: boolean;
    loadingMore?: boolean;
    style?: any;
    contentContainerStyle?: any;
    threshold?: number;                // 距离底部多少触发加载，默认100
    ListFooterComponent?: React.ReactNode;
}

const DbScrollList = <T,>({
    data,
    renderItem,
    keyExtractor,
    onEndReached,
    onRefresh,
    refreshing = false,
    loadingMore = false,
    style,
    contentContainerStyle,
    threshold = 100,
    ListFooterComponent,
}: DbScrollListProps<T>) => {
    const scrollViewRef = useRef<ScrollView>(null);
    const onScroll = useCallback((e: NativeSyntheticEvent<NativeScrollEvent>) => {
        const { layoutMeasurement, contentOffset, contentSize } = e.nativeEvent;
        // 距离底部小于 threshold 时触发
        if (
            contentOffset.y + layoutMeasurement.height + threshold >= contentSize.height
            && !loadingMore
        ) {
            onEndReached && onEndReached();
        }
    }, [onEndReached, loadingMore, threshold]);

    return (
        <ScrollView
            ref={scrollViewRef}
            style={style}
            contentContainerStyle={contentContainerStyle}
            scrollEventThrottle={16}
            onScroll={onScroll}
            refreshControl={
                onRefresh ? (
                    <RefreshControl refreshing={refreshing} onRefresh={onRefresh} />
                ) : undefined
            }
        >
            {data.map((item, idx) => (
                <View key={keyExtractor ? keyExtractor(item, idx) : idx.toString()}>
                    {renderItem(item, idx)}
                </View>
            ))}
            {loadingMore ? (
                ListFooterComponent || (
                    <View style={{ padding: 16, alignItems: 'center' }}>
                        <ActivityIndicator size="small" />
                    </View>
                )
            ) : null}
        </ScrollView>
    );
};

export default DbScrollList;
